export default function addComponentLoader(componentMapping, scriptMapping, storeGetterImport) {
  baseAddComponentLoader(componentMapping, scriptMapping, storeGetterImport, false, null)
}

export function addSharedStoreComponentLoader(componentList, scriptMapping, storeGetterImport, storeDataComponentName) {
  baseAddComponentLoader(componentList, scriptMapping, storeGetterImport, true, storeDataComponentName)
}

export function importWithRetry(importer, retriesLeft = 3) {
  if (retriesLeft === 0) {
    throw new Error('Failed to load component after 3 retries')
  }

  return new Promise((resolve, reject) => {
    importer()
      .then(resolve)
      .catch((error) => {
        console.warn(error, 'Failed to load component, retrying...')
        setTimeout(async () => {
          try {
            // Recursively call the function again, decreasing the retry count
            const result = await importWithRetry(importer, retriesLeft - 1)
            resolve(result) // Resolve the promise with the result from the recursive call
          } catch (err) {
            reject(err) // Reject the promise if the recursive call fails
          }
        }, 500)
      })
  })
}

async function sharedStoreHandler(componentMapping, storeGetterImport, storeDataComponentName) {
  // Find all component loaders on the page
  const vueComponentDivs = document.getElementsByClassName('vue-component-loader')

  // Filter to just the ones in our componentMapping
  const relevantVueComponentDivs = Array.from(vueComponentDivs).filter(
    (div) => div.dataset.componentName in componentMapping
  )

  if (relevantVueComponentDivs.length === 0) {
    // None of the components we're looking for are on the page, so just exit.
    return
  }

  // Initialize the store! If storeDataComponentName is specified, that component's data
  // will be used to initialize the store.
  let vuexStore
  const { default: storeOrStoreGetter } = await storeGetterImport()
  if (typeof storeOrStoreGetter === 'function') {
    vuexStore = storeOrStoreGetter(storeDataComponentName)
  } else {
    vuexStore = storeOrStoreGetter
  }

  // Initialize each of the components in our mapping that are on the page,
  // hooking them all into the same vuex store.
  for (let div of relevantVueComponentDivs) {
    const componentData = div.dataset
    const componentName = componentData.componentName
    const props = JSON.parse(componentData.props)
    createVueComponent(div, componentMapping[componentName], props, vuexStore)
  }
}

function parseDataAttributes(element) {
  // Process each of the element's data attributes, converting them to the appropriate type.
  // This is necessary because the data attributes are stored as strings, but we sometimes include properties
  // of other types represented as strings (i.e.: JSON objects, booleans, etc.)
  const data = {}
  for (const [key, value] of Object.entries(element.dataset)) {
    try {
      // Try to parse as JSON first
      data[key] = JSON.parse(value)
    } catch {
      // If JSON parsing fails, try to convert to appropriate type
      if (value === 'true') {
        data[key] = true
      } else if (value === 'false') {
        data[key] = false
      } else if (value === 'null') {
        data[key] = null
      } else if (value === 'undefined') {
        data[key] = undefined
      } else if (!isNaN(value) && value.trim() !== '') {
        // Convert to number if it's a valid number
        data[key] = Number(value)
      } else {
        // Keep as string if no other conversion applies
        data[key] = value
      }
    }
  }
  return data
}

async function unorderedHandler(componentMapping, storeGetterImport) {
  const vueComponentDivs = document.getElementsByClassName('vue-component-loader')

  for (let div of vueComponentDivs) {
    const componentData = parseDataAttributes(div)
    const componentName = componentData.componentName

    if (componentName in componentMapping) {
      const props = componentData.props

      // Here, we expect that each component, if it has a store,
      // has a store that is independent from the other components being loaded.
      let vuexStore
      if (storeGetterImport) {
        const { default: storeOrStoreGetter } = await storeGetterImport()
        if (typeof storeOrStoreGetter === 'function') {
          vuexStore = storeOrStoreGetter(componentName)
        } else {
          vuexStore = storeOrStoreGetter
        }
      }
      createVueComponent(div, componentMapping[componentName], props, vuexStore)
    }
  }
}

function createVueComponent(div, definition, props, store) {
  const el = div.querySelector('.vue-component')

  if (!el || el.hasAttribute('data-v-app')) {
    // Either this .vue-component-loader div has no .vue-component child, or Vue already mounted an
    // app on it, which is what the data-v-app attribute marks. In both cases it has already been
    // processed, and there's nothing we need to do. Without the data-v-app check, every
    // `agra:need-vue-components` event would mount a second app on top of each component that is
    // already on the page, throwing away whatever state it was holding.
    return
  }

  // Transform existing list of classes ['class-a', 'class-b'] from the element into the format:
  // {
  //    class-a: true,
  //    class-b: true
  // }
  const classes = Object.fromEntries(Array.from(el.classList).map((val) => [val, true]))

  window.createRootVueComponent(el, definition, {...props, class: classes}, store)
}

function scriptHandler(scriptMapping) {
  const scriptDivs = document.getElementsByClassName('script-loader')

  for (let div of scriptDivs) {
    const scriptData = parseDataAttributes(div)
    const scriptName = scriptData.scriptName
    if (scriptName in scriptMapping) {
      scriptMapping[scriptName]()
    }
  }
}

function baseAddComponentLoader(componentCollection, scriptMapping, storeGetterImport, sharedStore = false, storeDataComponentName) {
  function handler() {
    if (sharedStore) {
      sharedStoreHandler(componentCollection, storeGetterImport, storeDataComponentName)
    }
    else {
      unorderedHandler(componentCollection, storeGetterImport)
    }
    scriptHandler(scriptMapping)
  }

  document.addEventListener('translations:loaded', () => {
    handler()
  })

  // The above event will not be fired again for our "remote" forms,
  // but we still need the vue component to be re-placed on the page.
  // Therefore, we fire this event for remote forms on update
  // if reloading vue components is needed.
  document.addEventListener('agra:need-vue-components', () => {
    handler()
  })
}
